import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import DayPicker from 'react-day-picker';
import { put } from 'helpers/apiHelpers';
import FormLabel from '@material-ui/core/FormLabel';
import Button from 'components/CustomButtons/Button';
import { toast } from 'react-toastify';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { makeStyles } from '@material-ui/styles';
import {
  blackColor,
  cadetBlueGrayColor,
  grayColor,
  navyColor,
  whiteColor,
  turquoiseColor,
} from 'assets/jss/material-dashboard-pro-react';
import RegularCheckbox from 'components/Checkbox/RegularCheckbox';
import { Switch } from '@material-ui/core';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import ChevronIcon from 'assets/img/ChevronIcon';
import classNames from 'classnames';

import DeleteBagConfirmModal from './DeleteBagConfirmModal';

const useStyles = makeStyles({
  labelHorizontal: {
    ...extendedFormsStyle.labelHorizontal,
    marginLeft: '12px',
    marginBottom: '0',
    color: grayColor[14],
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '24px',
  },
  orderBox: {
    padding: '16px',
    borderRadius: '14px',
    background: grayColor[13],
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  title: {
    margin: '0',
    color: grayColor[14],
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '120%',
  },
  multiBagToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 'auto',
  },
  multiBagToggleLabel: {
    color: grayColor[14],
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '120%',
    whiteSpace: 'nowrap',
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  dateText: {
    margin: '0',
    color: grayColor[16],
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '32px',
  },
  bagIdWrapper: {
    margin: '0',
    padding: '4px 8px',
    borderRadius: '10px',
    backgroundColor: whiteColor,
    color: blackColor,
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  bagId: {
    marginRight: '8px',
  },
  checkbox: {
    padding: '0',
  },
  checkboxRow: {
    alignItems: 'center',
    margin: '20px 0 32px',
    display: 'flex',
  },
  dayPickerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
    width: '100%',
  },
  dayContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  dayNumber: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '400',
    color: grayColor[16],
    transition: 'background-color 0.2s ease',
  },
  dayNumberDisabled: {
    color: grayColor[20],
  },
  dayNumberSelected: {
    backgroundColor: turquoiseColor,
    color: whiteColor,
  },
  dayNumberToday: {
    color: `${turquoiseColor} !important`,
  },
  dayPicker: {
    width: '100%',
    '& .DayPicker-Month': {
      width: '100%',
      borderSpacing: '0',
      margin: '16px 0 0 0',
    },
    '& .DayPicker-Caption > div': {
      fontSize: '18px',
      fontWeight: '500',
      color: blackColor,
      textTransform: 'capitalize',
      marginLeft: '-8px',
    },
    '& .DayPicker-Weekday': {
      padding: '8px 4px',
      color: grayColor[20],
      fontSize: '14px',
      fontWeight: '500',
    },
    '& .DayPicker-Day': {
      margin: '10px',
      padding: '0',
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
    },
    '& .DayPicker-Day--selected': {
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
    },

    '& .DayPicker-Day--disabled': {
      cursor: 'not-allowed',
    },
  },
  navButton: {
    position: 'absolute',
    top: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: '0',
    '&:hover': {
      opacity: 0.7,
    },
    '& svg': {
      width: '12px',
      height: '12px',
      color: grayColor[16],
    },
  },
  navButtonPrev: {
    right: '32px',
    '& svg': {
      transform: 'rotate(90deg)',
    },
  },
  navButtonNext: {
    right: '0',
    '& svg': {
      transform: 'rotate(-90deg)',
    },
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  deleteButton: {
    width: '100%',
    minHeight: '40px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '400',
    margin: '0',
    padding: '4px 24px',
    '&:hover, &:focus': {
      boxShadow: 'none',
    },
  },
  switch: {
    marginRight: '-12px',
  },
  customSwitchBase: {
    padding: '4px',
    color: cadetBlueGrayColor,
    '& + $customBar': {
      backgroundColor: grayColor[5],
      opacity: 1,
    },
    '&$customChecked + $customBar': {
      backgroundColor: cadetBlueGrayColor,
      opacity: 1,
    },
  },
  customChecked: { color: navyColor },
  customBar: {
    width: '32px',
    height: '16px',
    borderRadius: 9999,
    opacity: 1,
  },
});

const CustomNavbar = ({
  onPreviousClick,
  onNextClick,
  showPreviousButton,
  showNextButton,
  className,
}) => {
  const classes = useStyles();

  return (
    <div className={className}>
      {showPreviousButton && (
        <button
          type="button"
          className={classNames(classes.navButton, classes.navButtonPrev)}
          onClick={() => onPreviousClick()}
          aria-label="Previous month"
        >
          <ChevronIcon />
        </button>
      )}
      {showNextButton && (
        <button
          type="button"
          className={classNames(classes.navButton, classes.navButtonNext)}
          onClick={() => onNextClick()}
          aria-label="Next month"
        >
          <ChevronIcon />
        </button>
      )}
    </div>
  );
};

const RenderDay = ({ day, bagInfo, selectedDays, isDietDay }) => {
  const classes = useStyles();

  const isMainDay = moment(day).isSame(bagInfo?.date, 'day');
  const isSelected = selectedDays.some(selectedDay =>
    moment(selectedDay).isSame(day, 'day')
  );
  const isToday = moment(day).isSame(new Date(), 'day');
  const isDisabled =
    !isMainDay &&
    (moment(day).isBefore(moment(), 'day') ||
      !isDietDay(moment(day).format('YYYY-MM-DD')));

  const dayNumberClasses = classNames(classes.dayNumber, {
    [classes.dayNumberDisabled]: isDisabled,
    [classes.dayNumberSelected]: isSelected,
    [classes.dayNumberToday]: isToday && !isSelected,
  });

  return (
    <div className={classes.dayContent}>
      <div className={dayNumberClasses}>{day.getDate()}</div>
    </div>
  );
};

const DeleteBagSection = ({
  bagInfo,
  dietInfo,
  isDietDay,
  isLoading,
  setPickerDate,
  isDisabledDay,
  setIsLoading,
  setCalendarNeedsUpdate,
  isSelectedDaySubscription,
  isSelectedDaySubscriptionIntent,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const MONTH_NAMES = moment.localeData().months();
  const WEEKDAY_NAMES_LONG = moment.localeData().weekdays();

  const [returnCost, setReturnCost] = useState(false);
  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [isMultipleDaysChange, setIsMultipleDaysChange] = useState(false);
  const [paidMutation, setPaidMutation] = useState(true);
  const [selectAllDays, setSelectAllDays] = useState(false);

  useEffect(() => {
    setSelectedDays([new Date(bagInfo?.date)]);
    setSelectAllDays(false);
  }, [bagInfo?.date]);

  const getAllAvailableDays = () => {
    const availableDays = [];
    const startDate = moment(bagInfo?.date).startOf('month');
    const endDate = moment(bagInfo?.date).add(6, 'months').endOf('month');

    const currentDate = startDate.clone();
    while (
      currentDate.isSameOrBefore(endDate) &&
      availableDays.length < dietInfo.dietLength
    ) {
      const dateStr = currentDate.format('YYYY-MM-DD');

      if (isDietDay(dateStr) && !currentDate.isBefore(moment(), 'day')) {
        availableDays.push(currentDate.toDate());
      }

      currentDate.add(1, 'day');
    }

    return availableDays;
  };

  useEffect(() => {
    if (isMultipleDaysChange && selectedDays.length > 1) {
      const availableDays = getAllAvailableDays();
      const allSelected = availableDays.every(availableDay =>
        selectedDays.some(selectedDay =>
          moment(selectedDay).isSame(availableDay, 'day')
        )
      );
      setSelectAllDays(allSelected && availableDays.length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, isMultipleDaysChange]);

  const handleSubmit = async () => {
    try {
      await put(`/v2/calendar/${dietInfo?.id}/cancel-bags`, {
        paidMutation,
        dates: selectedDays.map(date => moment(date).format('YYYY-MM-DD')),
      });
    } catch (error) {
      const { response } = error;
      switch (response?.status) {
        case 402:
          toast.info(t('singleDayDetails.bagDeletingIsImpossible.description'));
          break;
        case 409:
          setReturnCost(response?.data?.returnCost);
          setIsConfirmModalOpened(true);
          break;
        default:
          break;
      }
    }
  };

  const handleConfirm = () => {
    if (paidMutation || isSelectedDaySubscriptionIntent) {
      handleSubmit();
      return;
    }
    setIsConfirmModalOpened(true);
  };

  const handleSelectAllDays = () => {
    if (selectAllDays) {
      setSelectedDays([new Date(bagInfo?.date)]);
      setSelectAllDays(false);
    } else {
      const availableDays = getAllAvailableDays();
      setSelectedDays(availableDays);
      setSelectAllDays(true);
    }
  };

  return (
    <>
      <div className={classes.orderBox}>
        <div className={classes.headerRow}>
          <p className={classes.title}>{t('singleDayDetails.deleteBag')}</p>
          {!isSelectedDaySubscription && (
            <div className={classes.multiBagToggleRow}>
              <span className={classes.multiBagToggleLabel}>
                {t('singleDayDetails.applyToMultipleBags')}
              </span>
              <Switch
                color="default"
                className={classes.switch}
                classes={{
                  switchBase: classes.customSwitchBase,
                  checked: classes.customChecked,
                  bar: classes.customBar,
                }}
                checked={isMultipleDaysChange}
                onChange={() => setIsMultipleDaysChange(prev => !prev)}
              />
            </div>
          )}
        </div>

        {!isMultipleDaysChange && (
          <div className={classes.detailsRow}>
            <p className={classes.dateText}>
              {bagInfo?.date && moment(bagInfo.date).format('DD.MM.YYYY')}
            </p>
            <p className={classes.bagIdWrapper}>
              <span className={classes.bagId}>ID #{bagInfo?.id}</span>
              <CopyToClipboard text={String(bagInfo?.id)} />
            </p>
          </div>
        )}

        {isMultipleDaysChange && (
          <div className={classes.dayPickerWrapper}>
            <DayPicker
              className={classes.dayPicker}
              fromMonth={new Date()}
              months={MONTH_NAMES}
              month={new Date(bagInfo?.date)}
              weekdaysLong={WEEKDAY_NAMES_LONG}
              weekdaysShort={moment.localeData().weekdaysMin()}
              firstDayOfWeek={1}
              selectedDays={selectedDays}
              navbarElement={CustomNavbar}
              renderDay={day => (
                <RenderDay
                  day={day}
                  bagInfo={bagInfo}
                  selectedDays={selectedDays}
                  isDietDay={isDietDay}
                />
              )}
              onMonthChange={monthStart => setPickerDate(moment(monthStart))}
              disabledDays={day => {
                const isMainDay = moment(day).isSame(bagInfo?.date, 'day');
                if (isMainDay) {
                  return false;
                }

                return (
                  moment(day).isBefore(moment(), 'day') ||
                  !isDietDay(moment(day).format('YYYY-MM-DD'))
                );
              }}
              // eslint-disable-next-line consistent-return -- FIXME
              onDayClick={(day, { selected }) => {
                const isMainDay = moment(day).isSame(bagInfo?.date, 'day');

                if (
                  !selected &&
                  selectedDays.length > 0 &&
                  dietInfo.dietLength === selectedDays.length
                ) {
                  return toast.info(
                    `${t('singleDayDetails.allDaysHaveBeenUsedUp')} ${t(
                      'singleDayDetails.unselectSomeDaysToChooseAnother'
                    )}`
                  );
                }

                const isDayDisabled =
                  isDisabledDay(day, true) ||
                  moment(day).isBefore(moment(), 'day') ||
                  !isDietDay(moment(day).format('YYYY-MM-DD'));

                if (isDayDisabled && !isMainDay) {
                  return null;
                }

                const tmpSelectedDays = [...selectedDays];

                if (selected) {
                  const selectedIndex = tmpSelectedDays.findIndex(selectedDay =>
                    moment(selectedDay).isSame(day, 'day')
                  );
                  tmpSelectedDays.splice(selectedIndex, 1);
                } else {
                  tmpSelectedDays.push(day);
                }

                setSelectedDays(tmpSelectedDays);
              }}
            />
          </div>
        )}

        {isMultipleDaysChange && (
          <div className={classes.checkboxRow}>
            <RegularCheckbox
              classes={{ root: classes.checkbox }}
              id="selectAllDaysCheckbox"
              name="selectAllDays"
              checked={selectAllDays}
              onClick={handleSelectAllDays}
            />

            <FormLabel
              className={classes.labelHorizontal}
              htmlFor="selectAllDaysCheckbox"
            >
              {t('singleDayDetails.selectAllDietDays')}
            </FormLabel>
          </div>
        )}

        <div className={classes.checkboxRow}>
          <RegularCheckbox
            classes={{ root: classes.checkbox }}
            id="paidMutationsCheckbox"
            name="paidMutation"
            checked={paidMutation}
            onClick={() => {
              setPaidMutation(prev => !prev);
            }}
          />

          <FormLabel
            className={classes.labelHorizontal}
            htmlFor="paidMutationsCheckbox"
          >
            {t('singleDayDetails.includeTheCostOfChanges')}
          </FormLabel>
        </div>

        <div className={classes.actionsRow}>
          <Button
            color="danger"
            className={classes.deleteButton}
            onClick={handleConfirm}
          >
            {t('common.shared.delete')}
          </Button>
        </div>
      </div>
      <DeleteBagConfirmModal
        isOpened={isConfirmModalOpened}
        returnCost={returnCost}
        toggleModal={setIsConfirmModalOpened}
        dietInfo={dietInfo}
        isLoading={isLoading}
        paidMutation={paidMutation}
        dates={selectedDays.map(date => moment(date).format('YYYY-MM-DD'))}
        setIsLoading={setIsLoading}
        setCalendarNeedsUpdate={setCalendarNeedsUpdate}
      />
    </>
  );
};

export default DeleteBagSection;
